import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AnnotationCanvas from '../annotation/AnnotationCanvas';
import { srOnly } from '../organic/motion';
import WorkbenchShell from '../workbench/WorkbenchShell';
import WorkbenchDrawer from '../workbench/WorkbenchDrawer';
import { ToolButton, WorkbenchSlot, WorkbenchStage, useWorkbench } from '../workbench/WorkbenchContext';
import { validateEntries } from '../../utils/annotationGeometry';
import { cleanEntry } from '../../utils/fieldAnnotations';
import { isReadOnlyRefusal } from '../../utils/viewAs';

/**
 * Annotate, from the Field camera's Pro drawer (docs/UI_HUBS_AND_GESTURES_PLAN.md
 * section 5, phase 7; decision D17): the annotation workbench's own canvas
 * (components/annotation/AnnotationCanvas.js) over the photographs of the
 * specimen being photographed, so a collector can mark the cap, the stem and
 * the gills or pores -- and hands, rulers and the like on the Ignore layer --
 * without the AI hub.
 *
 * Since 2026-10-26 it is the analysis workbench's zero-scroll layout
 * (docs/ANALYSIS_WORKBENCH.md section 12): a full-screen WorkbenchShell in its
 * overlay variant, over the camera, which keeps running beneath. Nothing on
 * it scrolls or moves but a drawer's body; the picture fills the stage at its
 * own proportions and stays where it is while drawers open and close.
 *
 *   top bar    x (the way out), the title, the photo stepper (previous, the
 *              photo shown -- n/N, a ✓ when marked, opens the Photos
 *              drawer -- next), "n of N marked"
 *   stage      the canvas (placement="workbench"), the notices over it
 *   toolbar    the tools, the zoom, the layers; then the drawers' toggles,
 *              undo / redo / clear (editRow 2: within a thumb's reach), Keep
 *   drawers    Photos (each photo, a ✓ on those marked), Parts and help (the
 *              specimen, the underside, the colour key, this help), Tool options
 *
 * The stepper and the Photos drawer choose the photograph (the newest first);
 * each photograph keeps its own marks while another is drawn on, and **Keep
 * the marks** hands the changed ones to `onSave({[photoId]: {entries, width,
 * height}})`, each checked first by validateEntries, as the server would. The
 * camera writes them to the batch in the Field store (`batch.annotations[photoId]`,
 * utils/fieldAnnotations.js withAnnotations); the outbox's Annotations step
 * sends them, merged with the summary's tissue outline, in one PUT per
 * photograph once that photograph is on the server. Nothing here needs a
 * connection. Marks all rubbed out on a photograph remove its marks; a
 * photograph gone from the specimen while this is open takes its draft with it.
 *
 * A modal dialog: named, described by its help, focus in (to its title) and
 * back to the Annotate button, Tab kept inside. Escape closes the drawer that
 * holds focus (else the last opened), then the workbench; on the canvas it
 * cancels an outline or a trace first, then goes on the same way. With marks
 * not yet kept, closing asks first (Discard, or Keep annotating): the shell's
 * own unsaved-work question, told by `dirty` and worded by `discardText`, so no
 * Escape loses work.
 */
export default function FieldAnnotate({ photos, urls, initial = {}, initialPhotoId = null, onSave, onClose }) {
  const { t } = useTranslation(['field', 'viewAs']);
  const choosable = (photos || []).filter(p => urls?.[p.id]);
  const first = choosable.find(p => p.id === initialPhotoId) || choosable[choosable.length - 1];
  const [chosen, setPhotoId] = useState(first?.id || null);
  const [drafts, setDrafts] = useState(() => Object.fromEntries(choosable.map(p => [p.id, initial[p.id]?.entries || []])));
  const [sizes, setSizes] = useState(() => Object.fromEntries(choosable
    .filter(p => initial[p.id]).map(p => [p.id, { width: initial[p.id].width, height: initial[p.id].height }])));
  const [touched, setTouched] = useState(() => new Set());
  const [problem, setProblem] = useState('');
  const [saving, setSaving] = useState(false);
  const helpId = useId();
  // A photograph removed while this is open: the newest one left is shown, and its draft is not kept.
  const present = id => choosable.some(p => p.id === id);
  const photoId = present(chosen) ? chosen : (choosable[choosable.length - 1]?.id || null);
  const changed = [...touched].filter(present);
  const dirty = changed.length > 0;

  const change = (entries) => {
    setDrafts(d => ({ ...d, [photoId]: entries }));
    setTouched(s => (s.has(photoId) ? s : new Set([...s, photoId])));
    setProblem('');
  };

  const save = async () => {
    const changes = {};
    for (const id of changed) {
      const entries = (drafts[id] || []).map(cleanEntry);
      if (validateEntries(entries)) {
        setPhotoId(id);
        setProblem(t('annotate.invalid'));
        return;
      }
      changes[id] = { entries, width: sizes[id]?.width ?? null, height: sizes[id]?.height ?? null };
    }
    setSaving(true);
    try {
      await onSave(changes);
      onClose();
    } catch (e) {
      setSaving(false);
      setProblem(isReadOnlyRefusal(e) ? t('viewAs:notKept') : t('annotate.storeFailed'));
    }
  };

  const marked = id => (drafts[id] || []).length > 0;
  const many = choosable.length > 1;
  const at = choosable.findIndex(p => p.id === photoId);
  const nameOf = (p) => {
    const n = (photos || []).indexOf(p) + 1;
    return t('camera.trayPhoto', { n, view: t(`views.${p.view}`) });
  };

  return (
    <WorkbenchShell variant="overlay" title={t('annotate.title')} onClose={onClose} closeLabel={t('annotate.close')}
      busy={saving} describedBy={helpId} editRow={2} testId="field-annotate" dirty={dirty}
      discardText={{ title: t('annotate.discardTitle'), body: t('annotate.discardBody'), keep: t('annotate.keepEditing'),
                     discard: t('annotate.discard') }}
      switcher={many ? (
        <PhotoStepper t={t} photos={choosable} at={at} marked={marked} nameOf={nameOf} onPick={setPhotoId} />
      ) : null}>
      {many && (
        <WorkbenchSlot name="context">
          <span className="wb-chip" data-testid="field-annotate-count">
            {t('annotate.markedOf', { count: choosable.filter(p => marked(p.id)).length, total: choosable.length })}
          </span>
        </WorkbenchSlot>
      )}

      {many && (
        <WorkbenchDrawer id="photos" side="start" order={10} icon="🖼️" title={t('annotate.photos')}
          testId="field-annotate-photos-drawer">
          <PhotoList t={t} photos={choosable} urls={urls} photoId={photoId} marked={marked} nameOf={nameOf}
            onPick={setPhotoId} />
        </WorkbenchDrawer>
      )}

      {photoId && (
        <WorkbenchStage size={sizes[photoId] || null} testId="field-annotate-fit">
          <div style={{ width: '100%', height: '100%' }} data-testid="field-annotate-canvas" data-photo={photoId}>
            <AnnotationCanvas key={photoId} placement="workbench" src={urls[photoId]} value={drafts[photoId] || []}
              onChange={change} onImageSize={size => setSizes(s => ({ ...s, [photoId]: size }))}
              intro={<p id={helpId} style={{ margin: 0, fontSize: 13 }}>{t('annotate.help')}</p>} />
          </div>
        </WorkbenchStage>
      )}

      <WorkbenchSlot name="status">
        {problem && <p role="alert" style={as.problem} data-testid="field-annotate-problem">{problem}</p>}
      </WorkbenchSlot>

      <WorkbenchSlot name="save">
        <ToolButton className="wb-primary" icon="💾" label={t('annotate.save')} onClick={save} disabled={!dirty || saving}
          aria-busy={saving || undefined} data-testid="field-annotate-save" />
      </WorkbenchSlot>

    </WorkbenchShell>
  );
}

const Chevron = ({ d }) => (
  <svg aria-hidden="true" className="wb-chevron" viewBox="0 0 16 16" width="16" height="16">
    <path d={d} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * The top bar's photo stepper: the previous photograph, the one shown (its
 * thumbnail, n/N and a ✓ once marked; it opens the Photos drawer), the next.
 * The chevrons turn round in Hebrew with the shell's own back chevron.
 */
function PhotoStepper({ t, photos, at, marked, nameOf, onPick }) {
  const wb = useWorkbench();
  const current = photos[at];
  if (!current) return null;
  const open = wb.drawers.isOpen('photos');
  const where = { n: at + 1, total: photos.length, view: t(`views.${current.view}`) };
  const label = marked(current.id) ? t('annotate.currentMarked', where) : t('annotate.current', where);
  return (
    <div role="group" aria-label={t('annotate.stepper')} className="wb-switcher" data-testid="field-annotate-stepper">
      <button type="button" className="wb-btn" disabled={at <= 0} onClick={() => onPick(photos[at - 1].id)}
        aria-label={t('annotate.previous')} title={t('annotate.previous')} data-testid="field-annotate-prev">
        <Chevron d="M10 3 5 8l5 5" />
      </button>
      <button type="button" className="wb-btn" aria-label={label} title={nameOf(current)} aria-expanded={open}
        aria-controls="wb-drawer-photos" onClick={() => wb.drawers.toggle('photos')} data-testid="field-annotate-current"
        style={as.current}>
        <bdi dir="ltr" aria-hidden="true">{`${at + 1}/${photos.length}`}</bdi>
        {marked(current.id) && <span aria-hidden="true" style={as.tick}>✓</span>}
      </button>
      <button type="button" className="wb-btn" disabled={at >= photos.length - 1} onClick={() => onPick(photos[at + 1].id)}
        aria-label={t('annotate.next')} title={t('annotate.next')} data-testid="field-annotate-next">
        <Chevron d="M6 3l5 5-5 5" />
      </button>
    </div>
  );
}

/** The Photos drawer: every photograph the phone holds, the one shown pressed, a ✓ on those marked. */
function PhotoList({ t, photos, urls, photoId, marked, nameOf, onPick }) {
  const wb = useWorkbench();
  return (
    <div role="group" aria-label={t('annotate.photo')} style={as.list} data-testid="field-annotate-photos">
      {photos.map((p, i) => (
        <button key={p.id} type="button" aria-pressed={p.id === photoId} data-testid={`field-annotate-photo-${i}`}
          data-marked={marked(p.id) || undefined}
          style={{ ...as.pick, outline: p.id === photoId ? '3px solid var(--c-primary)' : 'none' }}
          onClick={() => {
            onPick(p.id);
            // On a phone the drawer covers the picture: chosen, it makes way for it.
            if (wb.compact) wb.drawers.close('photos');
          }}>
          <img src={urls[p.id]} alt={nameOf(p)} style={as.thumb} />
          {marked(p.id) && (
            <span style={as.badge}><span aria-hidden="true">✓</span><span style={srOnly}>{t('annotate.marked')}</span></span>
          )}
        </button>
      ))}
    </div>
  );
}

const as = {
  list: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 8 },
  pick: { position: 'relative', padding: 0, border: 'none', borderRadius: 8, overflow: 'hidden', cursor: 'pointer',
    background: 'none', minWidth: 48, minHeight: 48, aspectRatio: '1 / 1' },
  thumb: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  badge: { position: 'absolute', top: 4, insetInlineEnd: 4, minWidth: 20, height: 20, borderRadius: 999,
    background: 'var(--c-primary)', color: 'var(--c-on-accent)', fontSize: 13, lineHeight: '20px', textAlign: 'center' },
  current: { minWidth: 56, fontVariantNumeric: 'tabular-nums' },
  tick: { fontWeight: 700 },
  problem: { color: 'var(--c-danger)', margin: 0 },
};
